import { useCallback, useMemo, useRef, useState } from "react"
import { useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { prepareTextByFieldName } from "utils/string"

import { ImdfFeatureData, OccupantData } from "providers/venue/types"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useFeatureDetail } from "hooks/feature/useFeatureDetail"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import {
  excludeVatRefundCategory,
  hasVatRefund,
} from "extensions/iconsiam/utils"

import { MotionDiv } from "components/motions"

import { Head } from "components/Head"

import { usePlaceDetailMap } from "hooks/place/usePlaceDetailMap"
import { FeatureCard } from "extensions/iconsiam/components/domains/features"
import { ProductModal } from "extensions/iconsiam/components/domains/products"
import { ReservationModal } from "extensions/iconsiam/components/domains/reservation"
import { useGeoLocation } from "providers/geolocation"
import { FloorSelectionPanel } from "extensions/iconsiam/components/domains/occupant/FloorSelectionPanel"
import { useOccupantFloors } from "hooks/occupant/useOccupantFloors"
import { gtmPushDataLayerViewRecommendedMenu } from "utils/googletagmanager/viewRecommededMenu"
import { gtmPushDataLayerViewReservation } from "utils/googletagmanager/viewReservation"

const FADE_IN_TRANSITION = {
  initial: { opacity: 0 },
  enter: { opacity: 1 },
}

export const PlaceDetailKioskVPage = () => {
  const { dataLoaded, ...data } = useFeatureDetail()
  const {
    feature,
    name,
    id,
    categoryIconProp,
    boothName,
    location: locationName,
    level,
    isNavigatable = true,
    isNew,
    isComingSoon,
    category,
  } = data

  const isVatRefund = useMemo(
    () => hasVatRefund(feature as OccupantData),
    [feature]
  )
  const categoryText = useMemo(() => {
    if (!isVatRefund) return category
    return (
      getFeatureDetail(excludeVatRefundCategory(feature)) as IOccupantDetail
    )?.category
  }, [isVatRefund, feature, category])

  const { floorOptions, currentFloor, currentFloorOrdinal, hasFloors } =
    useOccupantFloors(feature)

  const [openProductModal, setOpenProductModal] = useState(false)
  const [openReservationModal, setOpenReservationModal] = useState(false)
  const dialogRef = useRef(null)
  const dialogHeight = +dialogRef.current?.clientHeight || 0

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppDetailPage`] || {}
  const { root } = styleOverrides

  const { locationTextKeys, locationSeparator, locationEmpty } =
    theme.components[`AppDetailPage`]?.defaultProps || {}

  const { goBack } = useAppUI()

  const { deviceLocation } = useGeoLocation()

  const isReadyToNavigate = !!deviceLocation && isNavigatable

  usePlaceDetailMap(feature as ImdfFeatureData, {
    enabled: !!feature,
    offset: { bottom: dialogHeight },
  })
  const navigate = useNavigate()
  const onNavigate = useCallback(() => {
    const locationId = feature?.properties?.feature?.id || id
    navigate(`/maps/dir/unknown/${locationId}`)
  }, [navigate, feature, id])

  const onViewPromotion = useCallback(() => {
    navigate(`/promotions/occupant/${id}`)
  }, [navigate, id])

  const onClickMultipleFloor = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`, { replace: true })
    },
    [navigate]
  )

  const { t } = useTranslation()

  const onClose = useCallback(() => {
    goBack()
  }, [goBack])

  const handleOpenProductModal = useCallback(() => {
    setOpenProductModal(true)
    gtmPushDataLayerViewRecommendedMenu(feature)
  }, [feature])
  const handleCloseProductModal = useCallback(() => {
    setOpenProductModal(false)
  }, [])

  const handleOpenReservationModal = useCallback(() => {
    setOpenReservationModal(true)
    gtmPushDataLayerViewReservation(feature)
  }, [feature])
  const handleCloseReservationModal = useCallback(() => {
    setOpenReservationModal(false)
  }, [])

  const location = useMemo(() => {
    const locationTextObj = {
      locationNameText: t(locationName),
      boothNameText: boothName ? t("Booth Text", { name: t(boothName) }) : "",
      floorText: level ? t("Level Text", { level: t(level) }) : "",
    }
    return prepareTextByFieldName(locationTextObj, locationTextKeys, {
      empty: locationEmpty,
      separator: locationSeparator,
    })
  }, [
    t,
    locationTextKeys,
    locationSeparator,
    locationEmpty,
    locationName,
    boothName,
    level,
  ])
  return (
    <>
      <Head title={`${t(name)} (${t("Place Detail")})`} />
      <MotionDiv
        sx={root}
        ref={dialogRef}
        initial="initial"
        animate="enter"
        transition={{ duration: 0.25, ease: [0.51, 0.92, 0.24, 1.15] }}
        variants={FADE_IN_TRANSITION}
      >
        {hasFloors && (
          <FloorSelectionPanel
            floorOptions={floorOptions}
            currentFloor={currentFloor}
            currentFloorOrdinal={currentFloorOrdinal}
            onClickFloor={onClickMultipleFloor}
          />
        )}
        <FeatureCard
          open={true}
          onClose={onClose}
          loading={!dataLoaded}
          data={{ ...data, location, category: categoryText } as any}
          onNavigate={onNavigate}
          onClickPromotionButton={onViewPromotion}
          categoryIconProp={categoryIconProp}
          isNavigatable={isReadyToNavigate}
          onClickReservationButton={handleOpenReservationModal}
          onClickRecommendedMenuButton={handleOpenProductModal}
          isNewShop={isNew}
          isComingSoon={isComingSoon}
          isVatRefund={isVatRefund}
          onClickMultipleFloor={onClickMultipleFloor}
        />
      </MotionDiv>
      <ProductModal
        data={data}
        open={openProductModal}
        onClose={handleCloseProductModal}
      />
      <ReservationModal
        data={data}
        open={openReservationModal}
        onClose={handleCloseReservationModal}
      />
    </>
  )
}
